import { Lock, MapPin, UserX } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import type { TimetableSlot } from '@/types/timetable';
import { REASON_LABEL, periodRange } from './timetableCopy';

/**
 * One period, drawn the same way in the week grid and on the day page.
 *
 * Two states, and they are drawn differently because they mean different things
 * to whoever is reading:
 *
 *   Available    an ordinary lesson. Quiet, because most of them are.
 *   Unavailable  the teacher has said they cannot take it. Amber and named,
 *                because the office reads this grid to find cover.
 *
 * `isLocked` is a past week or day: still readable, visibly not editable. A
 * register has already been taken against those periods, so changing who was
 * expected would rewrite what the day meant.
 */

export interface SlotCardProps {
  slot: TimetableSlot;
  /** Compact drops the class name — the week grid already has it in the column. */
  variant?: 'grid' | 'day';
  isLocked?: boolean;
  onClick?: (slot: TimetableSlot) => void;
}

export function SlotCard({ slot, variant = 'grid', isLocked = false, onClick }: SlotCardProps) {
  const isAway = slot.availability.status === 'UNAVAILABLE';
  const isInteractive = Boolean(onClick) && !isLocked;

  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="truncate text-sm font-[560] text-ink">{slot.subject.name}</p>
        {isLocked && <Lock className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />}
      </div>

      <p className="truncate text-xs text-ink-2">{slot.displayName}</p>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-ink-3">
        <span>{periodRange(slot.period.startTime, slot.period.endTime)}</span>
        {slot.roomName && (
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3 shrink-0" aria-hidden />
            {slot.roomName}
          </span>
        )}
      </div>

      {isAway && (
        <div className="flex flex-col gap-1 border-t border-warn/30 pt-2">
          <Badge tone="warning">
            <UserX className="mr-1 inline h-3 w-3" aria-hidden />
            {slot.availability.reason ? REASON_LABEL[slot.availability.reason] : 'Not available'}
          </Badge>
          {slot.availability.note && variant === 'day' && (
            <p className="text-2xs text-ink-2">{slot.availability.note}</p>
          )}
        </div>
      )}
    </>
  );

  const className = cn(
    'flex w-full flex-col gap-1.5 rounded-field border p-2.5 text-left transition-colors',
    isAway ? 'border-warn/45 bg-warn-bg' : 'border-line bg-surface',
    isLocked && 'opacity-70',
    isInteractive &&
      'hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
    variant === 'day' && 'gap-2 p-3.5',
  );

  if (!isInteractive) {
    return <div className={className}>{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => onClick?.(slot)}
      aria-label={`${slot.subject.name}, ${slot.displayName}, period ${slot.period.periodNumber} — change availability`}
      className={className}
    >
      {body}
    </button>
  );
}
